// Roles — Office admin. The `roles` table: the four system roles, read-only,
// and any custom role an admin makes.
//
// Neither the classic app nor the rebuild has ever called these four routes.
// `client/src/services/api.js:115-118` declares wrappers for them and nothing
// imports those wrappers, so the endpoints have been live and unreachable —
// this screen is the first thing in either tree that opens them.
//
// ── The contract, read off server/routes/admin.js ───────────────────────────
//
//   GET    /api/admin/roles       :368  → [ { id, name, permissions, is_system,
//                                            created_at } ]
//   POST   /api/admin/roles       :386  { name, permissions } → 201 the role
//   PUT    /api/admin/roles/:id   :419  { permissions }        → 200 the role
//   DELETE /api/admin/roles/:id   :459                          → 200 { message }
//
// Mounted at `/api/admin` (server/server.js:252). `GET` is `SELECT *` on the
// table (server/database.js:50-57), with `permissions` JSON.parsed by the
// handler, so a row is exactly: id · name · permissions · is_system ·
// created_at. Nothing else exists, and RolesContract.test asserts each field
// against the CREATE TABLE.
//
// ── Four things the server does that a naive screen would get wrong ─────────
//
// **1. The gate is `checkRole('admin')`, not a permission.** All four routes
// carry it (admin.js:368, 386, 419, 459), and `checkRole`
// (server/middleware/auth.js:117-124) is `allowedRoles.includes(req.user.role)`
// and nothing else — there is no `extra_permissions` escape from it. So this
// screen gates on `useRole() === 'admin'`, NOT on `useCan('roles', 'read')`.
// The admin baseline does grant `roles: [create, read, update, delete]`
// (server/database.js:1000), so for an admin the two agree; for anybody handed
// `roles` through per-user extras they do not, and `useCan` would show them a
// screen every request behind it answers 403 to. That is the exact defect
// server/utils/__tests__/roleGateDivergence.test.js exists to count.
//
// **2. PUT ignores `name`.** The handler updates one column and only when the
// body carried it — `if (permissions) db.prepare('UPDATE roles SET
// permissions = ? WHERE id = ?')` (admin.js:441-444). A rename typed into an
// edit form would be accepted with a 200 and silently discarded. So the name
// is read-only once the role exists, and the form says why rather than
// offering a field that does nothing.
//
// **3. A duplicate name is a 500, not a 400.** `roles.name` is `TEXT UNIQUE
// NOT NULL` (database.js:52). The form checks the name against the list it
// already loaded and refuses in words, because that is the faster answer and
// the one that can point at the row.
//
// The server is the authority and now says the same thing: the INSERT is
// preceded by a `COLLATE NOCASE` uniqueness check answering
// 409 "A role called X already exists." (admin.js). It used to let the UNIQUE
// constraint throw into the catch and come back as 500 "Failed to create
// role", which reads as "the server is broken" rather than "pick another
// name" — fixed 2026-09-05, with the case-insensitivity pinned, because
// SQLite compares TEXT case-sensitively and 'Foreman'/'foreman' would
// otherwise be two rows and one name to a person.
//
// `nameProblem` still cannot see a name created in another tab between load
// and submit; that one gets the 409, and the refusal is surfaced either way,
// never swallowed.
//
// **4. Delete refuses with a countable reason.** A system role is 400 "Cannot
// delete system roles"; a role somebody holds is 400 "Cannot delete role.
// N user(s) are assigned this role." (admin.js:470-477). Both sentences are
// written for people, and `useAction` → `describeError` puts an answered
// 400's own sentence on screen unchanged rather than a status code.
//
// ── The warning this screen has to carry ────────────────────────────────────
//
// A custom role is a row in `roles`; `users.role` stores its NAME, and
// `getUserPermissions` LEFT JOINs `roles ON r.name = u.role`
// (server/utils/permissions.js:66-72), so its permissions really are the
// holder's baseline. But most doors in this application are guarded by role
// NAME, not by permission: `checkRole(` outnumbers `checkPermission(` across
// server/routes/*.js, plus inline `req.user.role !== 'admin'` tests
// (server/routes/timeEntries.js:139, server/routes/timesheets.js:550). A
// custom role passes none of the first kind, whatever is ticked here.
//
// So the screen says so, in a Notice on every custom role. It is the
// difference between an admin who understands why their new role cannot open
// Invoices and one who thinks the app is broken.
//
// ── Why that Notice no longer quotes two numbers ────────────────────────────
//
// It used to read "49 route guards name roles directly … against 6 that read
// permissions", and this header said the same. On 2026-09-11 the tree measured
// **49 and 22** — `checkPermission(` had grown from 6 to 22 when activity.js,
// timeEntries.js and timesheets.js were converted on 2026-09-09
// (docs/PERMISSION_MODEL.md §2.3). So the screen had been telling admins a
// figure that was wrong by 3.7x, as a statement of fact about their own app.
//
// The guard that was supposed to keep the sentence honest only asserted
// `byRole > byPermission` and `byRole > 20` — both still true while the quoted
// number rotted, which is why nothing failed. A count rendered into a UI is
// arithmetic, and CLAUDE.md is explicit that arithmetic gets trusted.
//
// The claim is therefore stated QUALITATIVELY ("most"), which is what the
// guard can actually check, and OfficeRoles.test.js now refuses any hardcoded
// guard count in this file. The honest fix for a number that goes stale is to
// stop rendering the number, not to re-type it and wait.

import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { post, put, del } from '../../shared/http';
import { useRole } from '../../../hooks/usePermission';
import {
  DataTable, Pill, FwButton, FwField, FwInput, CheckRow, Sheet, Notice, Skeleton, EmptyState,
} from '../../../ui/fw';
import {
  useOfficeQuery, useAction, PageHeader, Toolbar, SearchBox, Panel, Split, DetailList, DetailRow,
  LoadError, FormError, ConfirmSheet, SheetFooter, matches, fmtDate,
} from '../records/OfficePage';
import './admin.css';

// ── the contract, as data ───────────────────────────────────────────────────

/**
 * Every path this screen calls. A table rather than four string literals in
 * the JSX, so RolesContract.test can check each against the route file —
 * CLAUDE.md, "Declare a contract once, and check it against the server".
 */
export const ROLE_PATHS = {
  list: '/admin/roles',
  create: '/admin/roles',
  update: (id) => `/admin/roles/${id}`,
  remove: (id) => `/admin/roles/${id}`,
  known: '/admin/permissions/known',
};

/** The react-query key every roles query sits under. */
export const ROLES_KEY = ['office', 'roles'];

/** The four names `checkRole` is written with. A custom role is never one of them. */
export const SYSTEM_ROLE_NAMES = ['admin', 'manager', 'accounting', 'worker'];

// ── pure helpers, exported so the suite can pin them without a DOM ──────────

/**
 * Is this row a system role?
 *
 * `is_system` is `INTEGER DEFAULT 0` (database.js:54) so it arrives as 0 or 1,
 * but a row echoed back from POST/PUT is whatever better-sqlite3 handed over
 * and a hand-written fixture may say `true`. Truthiness covers all of them.
 *
 * A row with no `is_system` at all is treated as a SYSTEM role — the safe
 * direction. Guessing "custom" would offer Edit and Delete on something the
 * server refuses, which is the ghost button this file exists to avoid; guessing
 * "system" only withholds two controls until the row loads properly.
 */
export function isSystemRole(row) {
  if (!row) return true;
  if (!('is_system' in row)) return true;
  return !!row.is_system;
}

/**
 * The permissions object off a row, always an object.
 *
 * The handler JSON.parses the column before sending, so this is normally
 * `{ resource: [actions] }` already. A row whose JSON was malformed would have
 * thrown in the handler and come back a 500, so the only shapes that reach
 * here are an object or something absent.
 */
export function permissionsOf(row) {
  const p = row && row.permissions;
  return p && typeof p === 'object' && !Array.isArray(p) ? p : {};
}

/** How many (resource, action) grants a role carries. */
export function grantCount(perms) {
  return Object.values(perms || {}).reduce((n, a) => n + (Array.isArray(a) ? a.length : 0), 0);
}

/** How many resources it touches. */
export function resourceCount(perms) {
  return Object.entries(perms || {}).filter(([, a]) => Array.isArray(a) && a.length).length;
}

/**
 * `perms` with one (resource, action) flipped. Empty resources are dropped so
 * the object sent to the server stays tidy and `grantCount` cannot count a
 * resource whose list is `[]`.
 *
 * Same shape and same reasoning as `toggleExtra` in Users.js; deliberately not
 * shared, because that one is about per-user EXTRAS merged over a baseline and
 * this one is the baseline itself. Two callers, two meanings, one accidental
 * coupling avoided.
 */
export function togglePermission(perms, resource, action, on) {
  const next = {};
  Object.entries(perms || {}).forEach(([r, acts]) => {
    if (Array.isArray(acts) && acts.length) next[r] = [...acts];
  });
  const list = new Set(next[resource] || []);
  if (on) list.add(action); else list.delete(action);
  if (list.size) next[resource] = [...list]; else delete next[resource];
  return next;
}

/** Do two permission objects grant the same set? Order-insensitive. */
export function samePermissions(a, b) {
  const norm = (o) => JSON.stringify(
    Object.entries(o || {})
      .filter(([, acts]) => Array.isArray(acts) && acts.length)
      .map(([r, acts]) => [r, [...acts].sort()])
      .sort((x, y) => (x[0] < y[0] ? -1 : 1)),
  );
  return norm(a) === norm(b);
}

/**
 * Why this name cannot be used, or null.
 *
 * The server's own refusal for a duplicate is an unhelpful 500 (see the header),
 * so the two cases it CAN answer usefully are caught here first: an empty name
 * (`body('name').trim().notEmpty()`, admin.js:388, which answers 400 with a
 * validator array rather than a sentence) and a name already in the list.
 *
 * `existing` is the loaded rows. Comparison is case-insensitive and trimmed
 * because `UNIQUE` in SQLite is case-SENSITIVE by default on a TEXT column —
 * so 'Foreman' and 'foreman' are two different rows to the database and one
 * name to a person, and the second is worth stopping even though the server
 * would allow it.
 */
export function nameProblem(name, existing) {
  const n = String(name == null ? '' : name).trim();
  if (!n) return 'Give the role a name.';
  const clash = (existing || []).find((r) => String(r.name || '').trim().toLowerCase() === n.toLowerCase());
  if (clash) {
    return isSystemRole(clash)
      ? `"${clash.name}" is one of the four built-in roles. Pick another name.`
      : `A role called "${clash.name}" already exists.`;
  }
  return null;
}

/**
 * What a role is GRANTED, as a list to read rather than a grid to scan.
 *
 * ── The measurement this exists for ─────────────────────────────────────────
 *
 * Chromium, the built app, /roles at 390x780, the `manager` role opened,
 * 2026-09-11:
 *
 *   76 permission rows across 15 resources, every one disabled
 *   42 of them ticked
 *   the matrix starts at y=1395 and is 5763px tall
 *   the page becomes 7278px — 9.3 phone screens
 *
 * So "what can a manager actually do?" was answered by scrolling past 1395px
 * and picking 42 ticks out of 76 identical disabled rows. That is the owner's
 * *"still hard to navigate and fully make sense of what's what when first
 * looking at things"*, on the screen whose entire job is being legible.
 *
 * ── What it returns, and what it deliberately does NOT claim ────────────────
 *
 * `[ { resource, label, items: [ { action, label, description } ] } ]`,
 * carrying ONLY granted actions, in the order the server lists its resources
 * (`GET /api/admin/permissions/known`) so two roles read down in the same
 * order and can be compared.
 *
 * The labels are the SERVER'S — `descriptions[resource][action].label` — not
 * a second vocabulary invented here. A screen that renames a capability is a
 * screen that disagrees with the thing enforcing it.
 *
 * **This is the permission list, and the permission list is not the whole
 * access-control system.** docs/PERMISSION_MODEL.md §2 measures the two
 * disagreeing in BOTH directions: a manager creates invoices through
 * `checkRole` while holding no such grant, and accounting holds the widest
 * `invoices` grant there is and is refused by every invoice write route. So
 * this renders what the role is GRANTED and says so in those words. It must
 * never be captioned "what this role can do" — that is a different question,
 * and answering it from this data would be a plausible wrong answer, which
 * CLAUDE.md calls the worst outcome available here.
 *
 * A resource the server did not describe still appears, under its own key
 * with the raw action as the label: an ungrantable-looking blank is worse
 * than an ugly one, and a new server action must not vanish from this list
 * just because nobody wrote a sentence for it yet.
 */
export function grantedList(perms, resources, descriptions) {
  const held = perms && typeof perms === 'object' ? perms : {};
  const known = resources && typeof resources === 'object' ? resources : {};
  const desc = descriptions && typeof descriptions === 'object' ? descriptions : {};

  // Server order first, then anything the role holds that the server did not
  // list — so a grant can never be invisible here.
  const order = [...Object.keys(known)];
  Object.keys(held).forEach((r) => { if (!order.includes(r)) order.push(r); });

  const out = [];
  order.forEach((resource) => {
    const granted = Array.isArray(held[resource]) ? held[resource] : [];
    if (!granted.length) return;
    // Keep the server's action order where it has one, so two roles line up.
    const actionOrder = Array.isArray(known[resource]) ? known[resource] : [];
    const ordered = [
      ...actionOrder.filter((a) => granted.includes(a)),
      ...granted.filter((a) => !actionOrder.includes(a)),
    ];
    const d = desc[resource] || {};
    out.push({
      resource,
      label: resource.replace(/_/g, ' '),
      items: ordered.map((action) => ({
        action,
        label: (d[action] && d[action].label) || action,
        description: (d[action] && d[action].description) || null,
      })),
    });
  });
  return out;
}

/**
 * The sentence under a custom role's name.
 *
 * Never says "0 permissions" from a role that failed to load — a role whose
 * `permissions` never arrived renders as a system-shaped row with no matrix,
 * and this is only ever called with a row the list returned.
 */
export function grantSummary(perms) {
  const g = grantCount(perms);
  if (g === 0) return 'No permissions yet.';
  const r = resourceCount(perms);
  return `${g} permission${g === 1 ? '' : 's'} across ${r} resource${r === 1 ? '' : 's'}.`;
}

// ── the permission matrix ───────────────────────────────────────────────────

function PermissionMatrix({ role }) {
  const qc = useQueryClient();
  const known = useOfficeQuery([...ROLES_KEY, 'known'], ROLE_PATHS.known);
  const system = isSystemRole(role);
  const [draft, setDraft] = useState(() => permissionsOf(role));
  const [saved, setSaved] = useState(false);
  // A built-in role opens on the granted LIST; the 76-row grid is still one
  // tap away for anyone who needs to see what is not granted. A custom role
  // has to open on the grid, because the grid is how it is edited.
  const [showAll, setShowAll] = useState(false);
  const { busy, error, run } = useAction();

  // Re-seed when a different role is selected. `role.id` is the identity, so
  // this key is set by the caller (<PermissionMatrix key={role.id} …>) and the
  // component remounts rather than syncing in an effect.

  if (known.isPending) return <Panel title="Permissions"><Skeleton lines={5} /></Panel>;
  if (known.error) {
    return (
      <Panel title="Permissions">
        <LoadError error={known.error} fallback="Could not load the list of permissions." />
      </Panel>
    );
  }

  const resources = (known.data && known.data.permissions) || {};
  const descriptions = (known.data && known.data.descriptions) || {};
  const dirty = !samePermissions(draft, permissionsOf(role));

  const flip = (resource, action) => (on) => {
    if (system) return;
    setDraft((d) => togglePermission(d, resource, action, on));
    setSaved(false);
  };

  const save = async () => {
    const ok = await run(async () => {
      await put(ROLE_PATHS.update(role.id), { permissions: draft });
      qc.invalidateQueries({ queryKey: ROLES_KEY });
    }, 'Could not save this role.');
    if (ok) setSaved(true);
  };

  return (
    <Panel
      title="Permissions"
      actions={system ? null : (
        <span className="or-actions">
          {saved && !dirty ? <span className="or-muted">Saved.</span> : null}
          <FwButton onClick={save} loading={busy} disabled={!dirty}>Save permissions</FwButton>
        </span>
      )}
    >
      {system ? (
        <Notice tone="info" title="Built in, and not editable here">
          The four built-in roles are rewritten from server/database.js on every boot, so a change
          made here would be undone by the next deploy. The server refuses it outright
          (&ldquo;Cannot modify system roles&rdquo;). To give one person more than their role
          allows, use Extras on the Users screen instead.
        </Notice>
      ) : (
        <p className="or-muted">{grantSummary(draft)} Tick what this role may do, then save.</p>
      )}
      <FormError error={error} />

      {/* A built-in role is read-only, so the grid of 76 mostly-irrelevant
          disabled rows is the wrong default: measured at 390px on 2026-09-11,
          `manager` rendered 76 rows across 15 resources with 42 ticked, the
          grid starting at y=1395 and running 5763px. The granted list answers
          the question the screen is opened with, in the server's own words. */}
      {system ? (
        <div data-testid="role-grants">
          <p className="or-muted">
            {grantSummary(draft)} These are the capabilities the <strong>{role.name}</strong> role
            is granted. Anything not listed is not granted — the absence of a resource is the deny.
          </p>
          <div className="ad-grants">
            {grantedList(draft, resources, descriptions).map((group) => (
              <div className="ad-grants__group" key={group.resource} data-grant-resource={group.resource}>
                <h3 className="ad-grants__resource">{group.label}</h3>
                <ul className="ad-grants__list">
                  {group.items.map((item) => (
                    <li className="ad-grants__item" key={item.action} data-granted={`${group.resource}.${item.action}`}>
                      <span className="ad-grants__label">{item.label}</span>
                      {item.description ? <span className="ad-grants__desc">{item.description}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Notice tone="warn" title="This is the permission list, not every door">
            Some routes check the role NAME as well, so a built-in role can reach things this list
            does not mention and be refused things it does. Where the two disagree, the route is
            what happens — the differences are recorded in docs/PERMISSION_MODEL.md.
          </Notice>
          <div className="or-actions">
            <FwButton
              variant="ghost"
              onClick={() => setShowAll((v) => !v)}
              aria-expanded={showAll ? 'true' : 'false'}
            >
              {showAll ? 'Hide what is not granted' : 'Show what is not granted'}
            </FwButton>
          </div>
        </div>
      ) : null}

      <div
        className="or-matrix"
        data-testid="role-matrix"
        hidden={system && !showAll}
      >
        {Object.entries(resources).map(([resource, actions]) => (
          <div key={resource} data-resource={resource}>
            <h3 className="or-matrix__resource">{resource.replace(/_/g, ' ')}</h3>
            {actions.map((action) => {
              const on = (draft[resource] || []).includes(action);
              const d = (descriptions[resource] || {})[action] || {};
              return (
                <CheckRow
                  key={action}
                  data-permission={`${resource}.${action}`}
                  checked={on}
                  disabled={system}
                  label={d.label || action}
                  meta={d.description || null}
                  onToggle={flip(resource, action)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ── one role ────────────────────────────────────────────────────────────────

function RoleDetail({ role, onClose, onDeleted }) {
  const qc = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const remove = useAction();
  const system = isSystemRole(role);
  const perms = permissionsOf(role);

  const doDelete = async () => {
    const ok = await remove.run(async () => {
      await del(ROLE_PATHS.remove(role.id));
      qc.invalidateQueries({ queryKey: ROLES_KEY });
    }, 'Could not delete this role.');
    if (ok) { setDeleteOpen(false); onDeleted(); }
  };

  return (
    <div className="or-form" data-testid="role-detail">
      <Panel
        title={role.name}
        actions={<FwButton variant="ghost" icon="x" aria-label="Close" onClick={onClose} />}
      >
        <DetailList>
          <DetailRow label="Kind">
            <Pill tone={system ? 'sched' : 'neutral'}>{system ? 'Built in' : 'Custom'}</Pill>
          </DetailRow>
          <DetailRow label="Grants">{grantSummary(perms)}</DetailRow>
          <DetailRow label="Created">{fmtDate(role.created_at)}</DetailRow>
        </DetailList>

        {system ? null : (
          <Notice tone="warn" title="A custom role does not open every door">
            <p>
              Most of this application checks the role NAME, not the permission list: most route
              guards name roles directly (admin, manager, accounting, worker) rather than reading
              permissions. Screens behind those stay shut for a custom role no matter what is
              ticked below.
            </p>
            <p>
              For one person who needs a little more than their role allows, Extras on the Users
              screen is the mechanism that works everywhere.
            </p>
          </Notice>
        )}

        <p className="or-muted">
          The name is fixed once the role exists — the server updates permissions only, so a rename
          here would be accepted and thrown away. Delete and recreate it to rename, after moving
          anyone who holds it.
        </p>

        {system ? null : (
          <div className="or-actions">
            <FwButton variant="quiet" onClick={() => { remove.clear(); setDeleteOpen(true); }}>Delete role…</FwButton>
          </div>
        )}
      </Panel>

      <PermissionMatrix key={role.id} role={role} />

      <ConfirmSheet
        open={deleteOpen}
        title={`Delete the ${role.name} role?`}
        confirmLabel="Delete role"
        tone="stop"
        busy={remove.busy}
        onConfirm={doDelete}
        onClose={() => setDeleteOpen(false)}
      >
        <FormError error={remove.error} />
        <Notice tone="warn" title="Nobody may be holding it">
          The server refuses while any account still has this role, and names how many. Move those
          people to another role first — this screen cannot do it for you, and Users can.
        </Notice>
        <p>Deleting a role removes the permission list only. No account, job or hour is touched.</p>
      </ConfirmSheet>
    </div>
  );
}

// ── new role ────────────────────────────────────────────────────────────────

export function NewRoleSheet({ open, existing, onClose, onCreated }) {
  const [name, setName] = useState('');
  const { busy, error, run, clear, setError } = useAction();

  const close = () => { clear(); setName(''); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    // The two refusals the server answers badly, caught before the request:
    // an empty name comes back as a validator ARRAY with no sentence, and a
    // duplicate comes back as a 500 saying "Failed to create role".
    const problem = nameProblem(name, existing);
    if (problem) { setError(problem); return; }
    const ok = await run(async () => {
      // An empty object is a valid `permissions` to `body('permissions').isObject()`
      // (admin.js:389), so the role is created with nothing ticked and the
      // matrix on its detail panel is where it gets filled in. One decision
      // per screen: name it here, grant it there.
      await post(ROLE_PATHS.create, { name: name.trim(), permissions: {} });
    }, 'Could not create the role.');
    if (ok) { setName(''); onCreated(); }
  };

  return (
    <Sheet
      open={open}
      title="New role"
      onClose={close}
      footer={<SheetFooter onCancel={close} busy={busy} submitLabel="Create role" formId="new-role" />}
    >
      <form id="new-role" className="or-form" onSubmit={submit} noValidate>
        <FormError error={error} />
        <FwField
          label="Name"
          htmlFor="nr-name"
          required
          hint="Lowercase, one word, no spaces — it is stored on each account as its role."
        >
          <FwInput id="nr-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </FwField>
        <Notice tone="info" title="It starts with nothing ticked">
          Create it, then use the permission list on its panel to say what it may do. Assign it to
          people from the Users screen.
        </Notice>
      </form>
    </Sheet>
  );
}

// ── the screen ──────────────────────────────────────────────────────────────

export default function Roles() {
  const role = useRole();
  const qc = useQueryClient();
  const isAdmin = role === 'admin';
  const rolesQ = useOfficeQuery([...ROLES_KEY, 'list'], ROLE_PATHS.list, { enabled: isAdmin });
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const list = useMemo(() => (Array.isArray(rolesQ.data) ? rolesQ.data : []), [rolesQ.data]);
  const rows = useMemo(() => list.filter((r) => matches(r, ['name'], search)), [list, search]);
  const selected = list.find((r) => r.id === selectedId) || null;

  // `measured` separates "the list is empty" from "the list never arrived".
  // react-query keeps the last good `data` through a failed refetch, so both
  // questions have to be asked, and a refusal must never render as "none".
  const measured = !rolesQ.error && !rolesQ.isPending && Array.isArray(rolesQ.data);
  const customCount = measured ? list.filter((r) => !isSystemRole(r)).length : null;

  if (!isAdmin) {
    return (
      <div className="or-page ad-page" data-screen="office-Roles">
        <PageHeader title="Roles" />
        <EmptyState icon="lock" title="Admins only">
          Roles can only be seen and changed by an administrator. This is the server&rsquo;s rule,
          not this screen&rsquo;s — every one of the four endpoints behind it checks the role name
          directly, so an extra permission cannot open it.
        </EmptyState>
      </div>
    );
  }

  const refresh = (closed) => {
    qc.invalidateQueries({ queryKey: ROLES_KEY });
    if (closed) setSelectedId(null);
  };

  // The card: the role's name, and BUILT IN / CUSTOM as the badge. That
  // distinction is this screen's status — it is the difference between a role
  // you may delete and one the server rewrites on every boot — so it goes
  // where a status goes rather than into a column. Both remaining columns fit
  // as facts, so nothing is hidden here.
  const columns = [
    { key: 'name', label: 'Role', role: 'title', render: (r) => <strong>{r.name}</strong> },
    {
      key: 'is_system',
      label: 'Kind',
      role: 'badge',
      render: (r) => <Pill tone={isSystemRole(r) ? 'sched' : 'neutral'}>{isSystemRole(r) ? 'Built in' : 'Custom'}</Pill>,
    },
    {
      key: 'grants',
      label: 'Permissions',
      align: 'right',
      role: 'fact',
      render: (r) => String(grantCount(permissionsOf(r))),
    },
    { key: 'created_at', label: 'Created', role: 'fact', render: (r) => fmtDate(r.created_at) },
  ];

  return (
    <div className="or-page ad-page" data-screen="office-Roles">
      <PageHeader
        title="Roles"
        meta={measured
          ? `${list.length} role${list.length === 1 ? '' : 's'} · ${customCount} custom`
          : null}
      >
        <FwButton icon="plus" onClick={() => setNewOpen(true)}>New role</FwButton>
      </PageHeader>

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Role name" label="Search roles" />
      </Toolbar>

      <LoadError error={rolesQ.error} fallback="Could not load the roles." />
      {rolesQ.isPending ? <Skeleton lines={5} /> : null}

      {measured && list.length === 0 ? (
        <EmptyState icon="lock" title="No roles">
          Not even the four built-in ones came back, which should not be possible — they are
          rewritten on every boot. Reload, and if it persists the roles table is the thing to look
          at.
        </EmptyState>
      ) : null}

      {list.length > 0 ? (
        <Split
          main={(
            <DataTable
              caption="Roles"
              columns={columns}
              rows={rows}
              onRowClick={(r) => setSelectedId(r.id)}
              empty={list.length ? 'No roles match that search.' : 'No roles yet.'}
            />
          )}
          aside={selected ? (
            <RoleDetail
              key={selected.id}
              role={selected}
              onClose={() => setSelectedId(null)}
              onDeleted={() => refresh(true)}
            />
          ) : null}
        />
      ) : null}

      <NewRoleSheet
        open={newOpen}
        existing={list}
        onClose={() => setNewOpen(false)}
        onCreated={() => { setNewOpen(false); refresh(); }}
      />
    </div>
  );
}
